import * as THREE from 'three';
import {
  MOTION_LIMITS,
  MOTION_TIMING,
  hasChange,
  moveSeconds,
  sceneStateAt,
  type Lesson,
  type ObjectState,
} from '@vrlearning/lesson-schema';

/**
 * Change within a lesson, played on the built objects
 * (docs/superpowers/specs/2026-09-28-wow-tools-design.md, "The engine").
 *
 * The scene at a step is computed, never remembered (lesson-schema's
 * `sceneStateAt`): forward by exactly one step animates from the step before
 * to this one; anything else -- back, a jump, joining, reloading -- snaps.
 * A new step while a change is still running snaps that change to its end
 * first, so nothing is ever left half-way. "↻ Nog eens" snaps back to the
 * step before and plays the change again.
 *
 * Within a step: a still moment so the "before" is seen; then everything
 * shown and hidden grows or shrinks at once (scale, not fade: fading merged
 * materials means sorting glitches); then every move together, eased at both
 * ends; then the highlight pulses twice. Time stands still while `hold()`
 * says so -- during a teleport fade, so the change is seen, not missed.
 */

/** The step properties this engine plays; the catalogue is tested against it. */
export const STEP_CHANGES = ['show', 'hide', 'move', 'highlight'] as const;

export type MotionTarget = {
  /** Placed, turned and scaled as the object's state. */
  readonly group: THREE.Object3D;
  /** What the highlight lights up. */
  readonly body: THREE.Object3D;
  /** An orbiting object's place is the frame loop's; only whether it is there is ours. */
  readonly orbiting?: boolean;
  /**
   * A library model (ADR-043): it walks and turns itself (render/models/runtime.ts), so its
   * place and turn are not ours either; showing, hiding and highlighting it are.
   */
  readonly walks?: boolean;
  /**
   * The object's name, a child of `group`. Kept upright above the object and
   * the same size however the object is turned or scaled: text sizes are a
   * headset rule (docs/04), and a turned lid must not swing its name round.
   */
  readonly label?: THREE.Object3D;
};

/** Where a name sits and how big it is when its object is neither turned nor scaled. */
type LabelRest = { position: THREE.Vector3; scale: THREE.Vector3 };

const DEG = Math.PI / 180;
/** As small as a shown object starts and a hidden one ends: 0 would be a degenerate matrix. */
const TINY = 0.01;

const easeOut = (u: number): number => 1 - (1 - u) ** 3;
const smoothstep = (u: number): number => u * u * (3 - 2 * u);
const clamp01 = (u: number): number => Math.min(1, Math.max(0, u));

const quaternionOf = (rot: readonly number[]): THREE.Quaternion =>
  new THREE.Quaternion().setFromEuler(new THREE.Euler(rot[0]! * DEG, rot[1]! * DEG, rot[2]! * DEG));

/** `scale` is the object's own, before the pop multiplies it. */
type Pop = { id: string; showing: boolean; scale: number };
type Move = {
  id: string;
  from: ObjectState;
  to: ObjectState;
  seconds: number;
  fromTurn: THREE.Quaternion;
  toTurn: THREE.Quaternion;
};
type Timeline = {
  index: number;
  t: number;
  pops: Pop[];
  moves: Move[];
  movesAt: number;
  highlight?: string;
  highlightAt: number;
  end: number;
};

/**
 * Whether something is actually on screen: it and every parent visible.
 * three.js raycasts objects whether or not they are visible.
 */
export function shown(object: THREE.Object3D): boolean {
  for (let at: THREE.Object3D | null = object; at; at = at.parent) if (!at.visible) return false;
  return true;
}

/** The bodies a ray may hit: never one that is hidden. */
export function pickable<T extends THREE.Object3D>(bodies: readonly T[]): T[] {
  return bodies.filter(shown);
}

export class SceneMotion {
  readonly #lesson: Lesson;
  readonly #objects: ReadonlyMap<string, MotionTarget>;
  readonly #hold: () => boolean;
  #timeline: Timeline | undefined;
  /** Materials the highlight has touched, and their emissive before. */
  readonly #lit = new Map<THREE.MeshStandardMaterial, { colour: THREE.Color; intensity: number }>();
  readonly #labels = new Map<string, LabelRest>();

  constructor(
    lesson: Lesson,
    objects: ReadonlyMap<string, MotionTarget>,
    options: { hold?: () => boolean } = {},
  ) {
    this.#lesson = lesson;
    this.#objects = objects;
    this.#hold = options.hold ?? ((): boolean => false);
    for (const [id, target] of objects) {
      if (target.label) {
        this.#labels.set(id, {
          position: target.label.position.clone(),
          scale: target.label.scale.clone(),
        });
      }
    }
  }

  /** A change is playing. */
  get running(): boolean {
    return this.#timeline !== undefined;
  }

  /**
   * Whether an object this step shows has yet to grow in: its hotspot badge
   * waits for it, rather than floating over nothing.
   */
  appearing(id: string): boolean {
    const timeline = this.#timeline;
    if (!timeline) return false;
    const pop = timeline.pops.find((candidate) => candidate.id === id);
    return pop?.showing === true && timeline.t < MOTION_TIMING.still + MOTION_TIMING.pop;
  }

  /** Straight to the scene at a step, with nothing playing. */
  snapTo(index: number): void {
    this.#timeline = undefined;
    this.#unlight();
    this.#apply(sceneStateAt(this.#lesson, index));
  }

  /** The running change, done: its step's scene exactly. */
  finish(): void {
    if (this.#timeline) this.snapTo(this.#timeline.index);
  }

  /** The class moved on by exactly one step: its change plays. */
  forward(index: number): void {
    this.finish();
    this.#play(index);
  }

  /** "↻ Nog eens": back to before the step, and its change again. */
  replay(index: number): void {
    this.finish();
    this.#play(index);
  }

  update(dt: number): void {
    const timeline = this.#timeline;
    if (!timeline || this.#hold()) return;
    timeline.t += dt;
    // A hair early rather than a frame late: frame times add up unevenly.
    if (timeline.t >= timeline.end - 1e-6) {
      this.snapTo(timeline.index);
      return;
    }
    this.#pose(timeline);
  }

  #play(index: number): void {
    const step = this.#lesson.steps[index];
    if (index <= 0 || !step || !hasChange(step)) {
      this.snapTo(index);
      return;
    }
    const from = sceneStateAt(this.#lesson, index - 1);
    const to = sceneStateAt(this.#lesson, index);
    this.snapTo(index - 1);

    const pops: Pop[] = [];
    const moves: Move[] = [];
    const seconds = new Map(
      (step.move ?? []).map((move) => [move.object, moveSeconds(move)] as const),
    );
    for (const [id, target] of this.#objects) {
      const a = from.get(id);
      const b = to.get(id);
      if (!a || !b) continue;
      if (a.visible !== b.visible) pops.push({ id, showing: b.visible, scale: a.scale });
      if (target.orbiting || target.walks) continue;
      const same =
        a.scale === b.scale &&
        a.pos.every((n, i) => n === b.pos[i]) &&
        a.rot.every((n, i) => n === b.rot[i]);
      if (!same) {
        moves.push({
          id,
          from: a,
          to: b,
          seconds: seconds.get(id) ?? MOTION_LIMITS.secondsDefault,
          fromTurn: quaternionOf(a.rot),
          toTurn: quaternionOf(b.rot),
        });
      }
    }

    const movesAt = MOTION_TIMING.still + (pops.length > 0 ? MOTION_TIMING.pop : 0);
    const highlightAt = movesAt + Math.max(0, ...moves.map((move) => move.seconds));
    const highlight =
      step.highlight !== undefined && this.#objects.has(step.highlight)
        ? step.highlight
        : undefined;
    this.#timeline = {
      index,
      t: 0,
      pops,
      moves,
      movesAt,
      highlightAt,
      ...(highlight !== undefined ? { highlight } : {}),
      end: highlightAt + (highlight !== undefined ? MOTION_TIMING.highlight : 0),
    };
  }

  #pose(timeline: Timeline): void {
    const { t } = timeline;
    const popping = clamp01((t - MOTION_TIMING.still) / MOTION_TIMING.pop);
    const started = t >= MOTION_TIMING.still;
    for (const pop of timeline.pops) {
      const group = this.#objects.get(pop.id)!.group;
      if (!started) continue;
      const grown = easeOut(popping);
      const factor = pop.showing ? TINY + (1 - TINY) * grown : 1 - (1 - TINY) * grown;
      group.visible = pop.showing || popping < 1;
      group.scale.setScalar(pop.scale * factor);
      // The name grows and shrinks with its object, but only as far as its own size.
      this.#steady(pop.id, pop.scale);
    }
    for (const move of timeline.moves) {
      const u = smoothstep(clamp01((t - timeline.movesAt) / move.seconds));
      const group = this.#objects.get(move.id)!.group;
      group.position.set(
        move.from.pos[0] + (move.to.pos[0] - move.from.pos[0]) * u,
        move.from.pos[1] + (move.to.pos[1] - move.from.pos[1]) * u,
        move.from.pos[2] + (move.to.pos[2] - move.from.pos[2]) * u,
      );
      group.quaternion.slerpQuaternions(move.fromTurn, move.toTurn, u);
      if (!timeline.pops.some((pop) => pop.id === move.id) || popping >= 1) {
        const scale = move.from.scale + (move.to.scale - move.from.scale) * u;
        group.scale.setScalar(scale);
        this.#steady(move.id, scale);
      }
    }
    if (timeline.highlight !== undefined && t >= timeline.highlightAt) {
      const u = clamp01((t - timeline.highlightAt) / MOTION_TIMING.highlight);
      // Twice up to the peak and back to nothing.
      this.#light(timeline.highlight, MOTION_TIMING.highlightPeak * Math.sin(2 * Math.PI * u) ** 2);
    }
  }

  #light(id: string, amount: number): void {
    const body = this.#objects.get(id)?.body;
    if (!(body instanceof THREE.Mesh)) return;
    const materials = (Array.isArray(body.material) ? body.material : [body.material]) as unknown[];
    for (const material of materials) {
      if (!(material instanceof THREE.MeshStandardMaterial)) continue;
      let before = this.#lit.get(material);
      if (!before) {
        before = { colour: material.emissive.clone(), intensity: material.emissiveIntensity };
        this.#lit.set(material, before);
      }
      if (material.userData['finish'] === 'glow') {
        // Lit already, in its own colour: it glows brighter and back, twice.
        // Whitening it and going back to nothing would put its glow out.
        material.emissiveIntensity = before.intensity * (1 + amount / MOTION_TIMING.highlightPeak);
      } else {
        material.emissive.setRGB(amount, amount, amount);
      }
    }
  }

  #unlight(): void {
    for (const [material, before] of this.#lit) {
      material.emissive.copy(before.colour);
      material.emissiveIntensity = before.intensity;
    }
    this.#lit.clear();
  }

  #apply(states: ReadonlyMap<string, ObjectState>): void {
    for (const [id, target] of this.#objects) {
      const state = states.get(id);
      if (!state) continue;
      target.group.visible = state.visible;
      target.group.scale.setScalar(state.scale);
      if (target.orbiting || target.walks) continue;
      target.group.position.set(...state.pos);
      target.group.rotation.set(state.rot[0] * DEG, state.rot[1] * DEG, state.rot[2] * DEG);
      this.#steady(id, state.scale);
    }
  }

  /**
   * Undoes the object's turn and its `scale` on its name: the name stays
   * straight above the object, at the height the scale puts the object's top,
   * and at its own size.
   */
  #steady(id: string, scale: number): void {
    const target = this.#objects.get(id);
    const rest = this.#labels.get(id);
    if (!target?.label || !rest) return;
    const turn = target.group.quaternion.clone().invert();
    target.label.position.copy(rest.position).applyQuaternion(turn);
    target.label.scale.copy(rest.scale).divideScalar(scale);
  }
}
